(() => {
  'use strict';
  const node = document.querySelector('[data-masi-commerce]');
  if (!node || window.MasiCart) return;
  const config = JSON.parse(node.textContent);
  const strings = JSON.parse(document.querySelector('[data-masi-commerce-strings]').textContent);
  config.strings = strings;
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const gifts = new Set(config.gifts.map(g => Number(g.id)));
  let queue = Promise.resolve(), busy = false;
  const api = { config, warning: '', uncertain: false, cart: null, isGift: item => gifts.has(Number(item.variant_id)),
    variant: id => Object.values(config.products).flatMap(p => p.variants).find(v => Number(v.id) === Number(id)),
    planFor: (variant,quantity) => variant?.plans?.find(p=>p.quantity===(Number(quantity)>=2?2:1)) || variant?.plan || null,
    planFor12: (variant,quantity) => { const ps=variant?.plans12||[]; return ps.find(p=>p.quantity===(Number(quantity)>=6?6:3)) || ps.find(p=>p.quantity<6) || ps[0] || null; },
    money: cents => new Intl.NumberFormat(config.locale, {style:'currency',currency:api.cart?.currency || config.currency}).format(cents/100)
  };
  // Plan families: 'm' = 4-week (1 / 2 adults), 'q' = 12-week (3 / 6 jars). Tier is chosen from the family's combined quantity.
  const familyOf = (variant,planId) => !planId ? null
    : variant?.plans?.some(p=>Number(p.id)===Number(planId)) ? 'm'
    : variant?.plans12?.some(p=>Number(p.id)===Number(planId)) ? 'q' : null;
  const tierFor = (variant,fam,quantity) => fam==='q' ? api.planFor12(variant,quantity) : api.planFor(variant,quantity);
  const unitOf = fam => fam==='q' ? 3 : 1; // jars per displayed unit (one 90-day supply = 3 jars)
  api.familyOf = familyOf; api.tierFor = tierFor; api.unitOf = unitOf;
  const managedLines = (cart,variant,fam) => cart.items.filter(item => Number(item.variant_id) === Number(variant?.id)
    && (fam ? familyOf(variant,item.selling_plan_allocation?.selling_plan?.id)===fam : !!familyOf(variant,item.selling_plan_allocation?.selling_plan?.id)));
  api.displayItems = cart => {
    const items = [];
    for (const item of cart.items) {
      const variant = api.variant(item.variant_id);
      const managed = familyOf(variant,item.selling_plan_allocation?.selling_plan?.id);
      const same = managed && items.find(line => line.masiSubscriptionGroup === managed && line.variant_id === item.variant_id);
      if (same) {
        same.quantity += item.quantity;
        same.original_line_price += item.original_line_price;
        same.final_line_price += item.final_line_price;
      } else items.push({...item, masiSubscriptionGroup: managed || false});
    }
    return items;
  };
  // Gift lines never qualify as paid merchandise, even if a discount fails to apply.
  api.checkoutError = cart => {
    const paid = cart?.items?.some(item => !api.isGift(item) && item.quantity > 0 && item.final_line_price > 0);
    if (!paid || !Number.isFinite(cart?.total_price) || cart.total_price <= 0) {
      return cart?.items?.some(api.isGift) ? strings.gift_checkout_required : strings.paid_required;
    }
    return '';
  };
  async function request(path, data) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    let response, body;
    try {
      response = await fetch(root + path, {method:data ? 'POST':'GET', credentials:'same-origin', cache:'no-store', signal:controller.signal, headers:{Accept:'application/json', ...(data ? {'Content-Type':'application/json'}:{})}, ...(data ? {body:JSON.stringify(data)}:{})});
      body = await response.json();
    } catch (_) {
      if (data) { api.uncertain = true; api.warning = strings.uncertain; }
      throw new Error(data ? strings.uncertain : strings.error);
    } finally { clearTimeout(timeout); }
    if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : strings.error);
    return body;
  }
  const read = () => request('cart.js');
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, {detail}));
  // ---- Cart mutation primitives shaped so Shopify's standard events report true deltas ----
  // Shopify publishes product_added_to_cart for increases (add/change/update) and product_removed_from_cart
  // only for change.js decreases, priced at the line's plan at that moment. So: decrease via change.js
  // before a tier change, increase via change.js after it, and swap plans with unchanged quantity (no event).
  const planIdOf = item => item?.selling_plan_allocation?.selling_plan?.id || null;
  const linesOf = (cart,variant,pred) => cart.items.filter(i => Number(i.variant_id)===Number(variant?.id) && !api.isGift(i) && pred(i));
  const famPred = (variant,fam) => fam ? (i => familyOf(variant,planIdOf(i))===fam) : (i => !planIdOf(i));
  const sum = lines => lines.reduce((n,l)=>n+l.quantity,0);
  async function decrease(cart,variant,pred,by) {
    for (let guard=0; by>0 && guard<20; guard++) {
      // One request per line where possible: prefer a line that covers the whole decrease, else the largest.
      const ls=linesOf(cart,variant,pred).sort((x,y)=>y.quantity-x.quantity), l=ls.find(x=>x.quantity===by)||ls[0]; if(!l) break;
      const n=Math.min(by,l.quantity); cart=await request('cart/change.js',{id:l.key,quantity:l.quantity-n}); by-=n;
    }
    return cart;
  }
  async function increase(cart,variant,pred,by) {
    const l=linesOf(cart,variant,pred)[0]; if(!l||by<=0) return cart;
    return request('cart/change.js',{id:l.key,quantity:l.quantity+by});
  }
  async function swapPlan(cart,variant,pred,planId) {
    for (let guard=0; guard<20; guard++) {
      const l=linesOf(cart,variant,pred).find(i=>Number(planIdOf(i)||0)!==Number(planId||0)); if(!l) break;
      cart=await request('cart/change.js',{id:l.key,quantity:l.quantity,selling_plan:planId||null});
    }
    return cart;
  }
  // ---- Plan-aware analytics (custom events; Shopify standard events are left untouched) ----
  const planInfo = (variant,planId) => {
    if (!planId) return {plan:'one-time',plan_label:'One-time',selling_plan_id:null,interval:null};
    const p=(variant?.plans||[]).find(x=>Number(x.id)===Number(planId)) || (Number(variant?.plan?.id)===Number(planId)?{...variant.plan,quantity:1}:null);
    if (p) return {plan:p.quantity>=2?'30-day-2-adults':'30-day',plan_label:p.quantity>=2?'30-day supply subscription, 2 adults':'30-day supply subscription',selling_plan_id:Number(planId),interval:'4 weeks'};
    const q=(variant?.plans12||[]).find(x=>Number(x.id)===Number(planId));
    if (q) return {plan:q.quantity>=6?'90-day-2-adults':'90-day',plan_label:q.quantity>=6?'90-day supply subscription, 2 adults':'90-day supply subscription',selling_plan_id:Number(planId),interval:'12 weeks'};
    return {plan:'subscription',plan_label:'Subscription',selling_plan_id:Number(planId),interval:null};
  };
  const ga4Ids = () => { try { return [...new Set((window.dataLayer||[]).filter(a=>a&&a[0]==='config'&&/^G-/.test(a[1])).map(a=>a[1]))]; } catch(_) { return []; } };
  const gaItem = (item,variant,info,quantity,unitPrice) => ({item_id:String(item.sku||item.variant_id),item_name:item.product_title||item.title,item_variant:String(item.variant_id),price:+(unitPrice/100).toFixed(2),quantity,item_category2:info.plan,selling_plan_id:info.selling_plan_id||undefined});
  api.track = (name, detail, ga, fbName) => {
    try { window.Shopify?.analytics?.publish?.(name, detail); } catch(_) {}
    // GA4 may not be configured yet on early page-load events (e.g. leftover rounding): retry briefly.
    const sendGa = (tries) => { try { const ids=ga4Ids(); if (window.gtag && ids.length) window.gtag('event', ga.name||name, {...ga.params, send_to: ids}); else if (tries>0) setTimeout(()=>sendGa(tries-1),500); } catch(_) {} };
    if (ga) sendGa(12);
    try { if (fbName && window.fbq) window.fbq('trackCustom', fbName, {value:detail.value,currency:detail.currency,contents:(detail.lines||[]).map(l=>({id:String(l.variant_id),quantity:l.quantity})),plans:(detail.lines||[]).map(l=>l.plan).join(',')}); } catch(_) {}
    emit('masi:analytics',{name,detail});
  };
  const snapshot = cart => { const m=new Map(); for (const i of cart?.items||[]) { if (api.isGift(i)) continue; const k=String(i.variant_id); const e=m.get(k)||{item:i,quantity:0,value:0,plans:new Map()}; e.quantity+=i.quantity; e.value+=i.final_line_price; e.plans.set(String(planIdOf(i)||''),(e.plans.get(String(planIdOf(i)||''))||0)+i.quantity); m.set(k,e);} return m; };
  const mainPlan = e => [...e.plans.entries()].sort((a,b)=>b[1]-a[1])[0]?.[0]||'';
  api.cartLines = cart => [...snapshot(cart).values()].map(e=>{const v=api.variant(e.item.variant_id),info=planInfo(v,mainPlan(e));return {variant_id:e.item.variant_id,product_title:e.item.product_title,sku:e.item.sku,quantity:e.quantity,value:e.value/100,...info};});
  function trackDiff(before, after, source) {
    if (!before || !after) return;
    const a=snapshot(before), b=snapshot(after), currency=after.currency||config.currency;
    for (const k of new Set([...a.keys(),...b.keys()])) {
      const x=a.get(k), y=b.get(k), dq=(y?.quantity||0)-(x?.quantity||0), dv=(y?.value||0)-(x?.value||0);
      const fromPlan=x?mainPlan(x):'', toPlan=y?mainPlan(y):fromPlan;
      if (!dq && fromPlan===toPlan) continue;
      const ref=(y||x).item, v=api.variant(ref.variant_id), info=planInfo(v,toPlan||null), from=planInfo(v,fromPlan||null);
      const name=dq>0?'masi_add_to_cart':dq<0?'masi_remove_from_cart':'masi_plan_change';
      const unit=dq? Math.abs(dv)/Math.abs(dq) : (y?.value||0)/Math.max(1,y?.quantity||1);
      const detail={source,currency,value:+(Math.abs(dv)/100).toFixed(2),value_delta:+(dv/100).toFixed(2),quantity_delta:dq,quantity:y?.quantity||0,from_plan:from.plan,from_selling_plan_id:from.selling_plan_id,...info,lines:[{variant_id:ref.variant_id,quantity:Math.abs(dq)||y?.quantity||0,plan:info.plan,selling_plan_id:info.selling_plan_id}]};
      api.track(name,detail,{params:{currency,value:detail.value,plan:info.plan,selling_plan_id:info.selling_plan_id||'',from_plan:from.plan,source,items:[gaItem(ref,v,info,Math.abs(dq)||y?.quantity||0,unit)]}},name==='masi_add_to_cart'?'MasiAddToCart':name==='masi_remove_from_cart'?'MasiRemoveFromCart':'MasiPlanChange');
    }
  }
  const gaItems = cart => { const m=new Map(); for (const i of cart.items||[]) { if (api.isGift(i)) continue; const k=i.variant_id+':'+(planIdOf(i)||''); const e=m.get(k)||{i,q:0,v:0}; e.q+=i.quantity; e.v+=i.final_line_price; m.set(k,e); } return [...m.values()].map(({i,q,v})=>{const vr=api.variant(i.variant_id);return gaItem(i,vr,planInfo(vr,planIdOf(i)),q,v/Math.max(1,q));}); };
  api.trackViewCart = source => {
    const cart=api.cart; if(!cart) return;
    const lines=api.cartLines(cart), currency=cart.currency||config.currency, value=+(cart.total_price/100).toFixed(2);
    const items=gaItems(cart);
    api.track('masi_view_cart',{source,currency,value,lines},{name:'view_cart',params:{currency,value,source,items}},'MasiViewCart');
  };
  api.trackCheckout = source => {
    const cart=api.cart; if(!cart) return;
    const lines=api.cartLines(cart), currency=cart.currency||config.currency, value=+(cart.total_price/100).toFixed(2);
    const items=gaItems(cart);
    api.track('masi_begin_checkout',{source,currency,value,lines,plans:lines.map(l=>l.plan)},{params:{currency,value,source,plans:lines.map(l=>l.plan).join(','),items}},'MasiBeginCheckout');
  };
  api.planInfo = planInfo;
  async function reconcile(cart) {
    api.warning = '';
    // Shopify can split paid lines for Buy X Get Y. Select the tier from their combined quantity.
    for (const product of Object.values(config.products)) for (const variant of product.variants) for (const fam of ['m','q']) {
      if (fam==='m' ? !variant.plans?.some(plan => plan.quantity === 2) : !(variant.plans12?.length > 1)) continue;
      let lines = managedLines(cart,variant,fam);
      let quantity = lines.reduce((sum,item) => sum + item.quantity,0);
      if (!quantity) continue;
      if (fam==='q' && quantity % 3) {
        // Leftover jars: round the 90-day family up to whole 3-jar supplies.
        const added = Math.ceil(quantity/3)*3 - quantity;
        quantity += added;
        api._source='rounding';
        cart = await increase(cart,variant,famPred(variant,'q'),added);
        api.notice = (({de:'Auf volle 90-Tage-Packungen aufgerundet: +[n] Glas',es:'Redondeado a suministros completos de 90 días: +[n] frasco(s)',fr:'Arrondi à des cures complètes de 90 jours : +[n] pot(s)',it:'Arrotondato a forniture complete da 90 giorni: +[n] vasetto/i'})[(document.documentElement.lang||'en').slice(0,2)] || 'Rounded up to full 90-day supplies: +[n] jar(s)').replace('[n]',added);
        lines = managedLines(cart,variant,fam);
      }
      const plan = tierFor(variant,fam,quantity);
      if (!plan) continue;
      for (let remaining = lines.length; remaining > 0; remaining--) {
        const line = managedLines(cart,variant,fam).find(item => Number(item.selling_plan_allocation.selling_plan.id) !== Number(plan.id));
        if (!line) break;
        cart = await request('cart/change.js',{line:cart.items.indexOf(line)+1,quantity:line.quantity,selling_plan:plan.id});
      }
    }
    const eligible = cart.items.some(item => !api.isGift(item) && item.selling_plan_allocation);
    const updates = {}, additions = [];
    for (const gift of config.gifts) {
      const lines = cart.items.filter(i => Number(i.variant_id) === Number(gift.id));
      const wanted = eligible && gift.available ? 1 : 0;
      lines.forEach((item,index) => {const qty = index === 0 ? wanted : 0; if(item.quantity !== qty) updates[item.key] = qty;});
      if (!lines.length && wanted) additions.push({id:gift.id,quantity:1,properties:{_masi_role:'welcome_gift'}});
      if(eligible && !gift.available) api.warning = strings.gift_error;
    }
    if (Object.keys(updates).length) cart = await request('cart/update.js',{updates});
    if (additions.length) {await request('cart/add.js',{items:additions}); cart = await read();}
    const charged = cart.items.filter(i => api.isGift(i) && i.final_line_price !== 0);
    if(charged.length) {
      cart = await request('cart/update.js',{updates:Object.fromEntries(charged.map(i=>[i.key,0]))});
      api.warning = strings.gift_error;
    }
    return cart;
  }
  function publish(cart) {
    api.cart = cart;
    const count = cart.items.filter(i => !api.isGift(i)).reduce((n,i)=>n+i.quantity,0);
    const icon=document.querySelector('#cart-icon-bubble');
    let bubble=icon?.querySelector('.ms-cart-count');
    if(icon&&!bubble){bubble=document.createElement('span');bubble.className='ms-cart-count';icon.append(bubble);}
    if(bubble){bubble.textContent=count;bubble.setAttribute('aria-label',String(count));bubble.hidden=!count;}
    emit('masi:cart:updated',cart);
    return cart;
  }
  function run(fn) {
    const result = queue.then(async()=>{
      busy = true;emit('masi:cart:busy',true);
      const before = api.cart;
      try { const cart = publish(await fn()); try { trackDiff(api._before||before, cart, api._source||'cart'); } catch(_) {} return cart; }
      finally {busy=false;api._source=null;api._before=null;emit('masi:cart:busy',false);}
    });
    queue = result.catch(()=>{});
    return result;
  }
  api.refresh = () => run(async()=>{
    const cart = await read(); api._before = cart;
    const unqualifiedGifts = cart.items.some(api.isGift) && !!api.checkoutError(cart);
    const updated = api.uncertain ? cart : await reconcile(cart);
    if (unqualifiedGifts && api.checkoutError(updated) && !api.uncertain && !api.warning) api.warning = strings.gift_checkout_required;
    return updated;
  });
  api.add = item => run(async()=>{
    if(api.uncertain) throw new Error(strings.uncertain);
    if(api.isGift({variant_id:item.id})) throw new Error(strings.error);
    const variant=api.variant(item.id);
    const fam=familyOf(variant,item.selling_plan);
    api._before = await read(); api._source='pdp';
    if(fam) {
      const cart=api._before;
      const quantity=Number(item.quantity)+managedLines(cart,variant,fam).reduce((sum,line)=>sum+line.quantity,0);
      await request('cart/add.js',{items:[{...item,selling_plan:(tierFor(variant,fam,quantity)||{id:item.selling_plan}).id}]});
    } else await request('cart/add.js',{items:[item]});
    // The product is already added. A gift failure must not invite a second product submission.
    try { return await reconcile(await read()); }
    catch(error) {
      api.warning=api.uncertain?strings.uncertain:strings.gift_error;
      try {return await read();} catch (_) {api.uncertain=true;api.warning=strings.uncertain;throw new Error(strings.uncertain);}
    }
  });
  api.change = (key,quantity,plan) => run(async()=>{
    if(api.uncertain) throw new Error(strings.uncertain);
    let cart = await read(); api._before = cart; api._source='drawer';
    const index = cart.items.findIndex(i=>i.key===key), item=cart.items[index];
    if(!item || api.isGift(item)) throw new Error(strings.stale);
    const variant=api.variant(item.variant_id),plans=[...(variant?.plans||[variant?.plan].filter(Boolean)),...(variant?.plans12||[])];
    const currentPlan=item.selling_plan_allocation?.selling_plan?.id;
    if(plan !== undefined && plan !== null && !plans.some(p=>Number(p.id)===Number(plan))) throw new Error(strings.stale);
    const managed=familyOf(variant,currentPlan);
    if (managed) {
      const pred=famPred(variant,managed), total=sum(linesOf(cart,variant,pred));
      const target = plan === undefined ? managed : familyOf(variant,plan);
      if (target === managed) {
        const q = quantity ?? total;
        if (q < total) { cart=await decrease(cart,variant,pred,total-q); if (q>0) cart=await swapPlan(cart,variant,pred,tierFor(variant,managed,q).id); }
        else if (q > total) { cart=await swapPlan(cart,variant,pred,tierFor(variant,managed,q).id); cart=await increase(cart,variant,pred,q-total); }
      } else {
        // Switch the whole family: keep the number of supplies/adults, convert jars (90-day = 3 jars per unit).
        const units=Math.max(1,Math.ceil(total/unitOf(managed))), jars=units*unitOf(target), tpred=famPred(variant,target);
        const existing=sum(linesOf(cart,variant,tpred));
        const newPlan=target ? tierFor(variant,target,jars+existing).id : null;
        if (jars < total) { cart=await decrease(cart,variant,pred,total-jars); cart=await swapPlan(cart,variant,pred,newPlan); }
        else { cart=await swapPlan(cart,variant,pred,newPlan); if (jars>total) cart=await increase(cart,variant,tpred,jars-total); }
      }
    } else {
      const fam=familyOf(variant,plan);
      if (quantity === undefined && fam) {
        const q=item.quantity*unitOf(fam), existing=sum(linesOf(cart,variant,famPred(variant,fam)));
        cart=await request('cart/change.js',{id:item.key,quantity:item.quantity,selling_plan:tierFor(variant,fam,q+existing).id});
        if (q>item.quantity) cart=await increase(cart,variant,famPred(variant,fam),q-item.quantity);
      } else {
        const body={id:item.key,quantity:quantity ?? item.quantity};
        if (body.quantity>0 && fam) {
          const existing=sum(linesOf(cart,variant,famPred(variant,fam)));
          body.selling_plan=tierFor(variant,fam,body.quantity+existing).id;
        } else if (plan !== undefined) body.selling_plan=plan;
        cart=await request('cart/change.js',body);
      }
    }
    try {return await reconcile(cart);}
    catch(error){
      api.warning=api.uncertain?strings.uncertain:strings.gift_error;
      try {return await read();} catch (_) {api.uncertain=true;api.warning=strings.uncertain;throw new Error(strings.uncertain);}
    }
  });
  api.isBusy=()=>busy;
  api.ready=queue;
  window.MasiCart=api;
})();
